'use client';

import React, { useEffect, useRef, useState } from 'react';

type MenuNode = {
  id: number;
  title: string;
  href: string;
  target?: string;
  children?: MenuNode[];
};

const SECTION_ROOT = '/microgrid/wet';
const WP_MENU_ENDPOINT =
  'https://wateredgetech.com/wp-json/wet/v1/menu?location=primary&tree=1&depth=3';

const INTERNAL_MENU_KEYWORDS = ['w2', '1099', 'contract', 'hub', 'session', 'internal', 'employee'];

const filterInternalMenuItems = (nodes: MenuNode[]): MenuNode[] =>
  nodes
    .filter((n) => {
      const label = n.title.replace(/<[^>]+>/g, '').toLowerCase();
      return !INTERNAL_MENU_KEYWORDS.some((kw) => label.includes(kw));
    })
    .map((n) => ({ ...n, children: n.children ? filterInternalMenuItems(n.children) : undefined }));

/* ---- GA helpers (local only, not in iframe) ---- */
function ga(name: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return;
    const g = (window as any).gtag;
    if (typeof g === 'function') g('event', name, params || {});
  } catch {}
}
const txt = (html: string) => html.replace(/<[^>]+>/g, '').trim();

/* ---- iframe helpers ---- */
const inIframe = (): boolean => {
  try { return window.top !== window.self; } catch { return true; }
};
const postParent = (msg: unknown) => { try { window.parent.postMessage(msg, '*'); } catch {} };

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<MenuNode[]>([]);
  const [openDesktop, setOpenDesktop] = useState<number | null>(null);
  const social = [
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/wateredgetech/',
      color: '#1877F2',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
          <path d="M22 12c0-5.522-4.477-10-10-10S2 6.478 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
        </svg>
      ),
    },
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/wateredgetech/',
      color: '#E1306C',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      ),
    },
    {
      label: 'TikTok',
      href: 'https://www.tiktok.com/@water.edge.techno',
      color: '#EE1D52',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.79a8.18 8.18 0 004.79 1.53V6.85a4.85 4.85 0 01-1.02-.16z"/>
        </svg>
      ),
    },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/water-edge-technologies/',
      color: '#0A66C2',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
    },
  ];
  const phone = { display: '(888) 714-7767', href: 'tel:1-888-714-7767' };
  const address = {
    text: '309 W Nakoma St, San Antonio, TX 78216',
    href: 'https://www.google.com/maps/search/?api=1&query=309+W+Nakoma+St,+San+Antonio,+TX+78216',
  };
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MENU_ENDPOINT, {
          method: 'GET',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          signal: ctrl.signal,
          cache: 'no-store',
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data: MenuNode[] = await r.json();
        if (!cancelled) {
          const filtered = Array.isArray(data) ? filterInternalMenuItems(data) : [];
          setItems(filtered);
          ga('menu_loaded', { source: 'wp', count: filtered.length });
        }
      } catch (e: any) {
        if (!cancelled) setItems([]);
        ga('menu_error', { source: 'wp', message: String(e?.message || e || 'unknown') });
      }
    })();
    return () => { cancelled = true; ctrl.abort(); };
  }, []);

  const isAnchor = (href?: string) => !!href && href.startsWith('#');

  // Smooth-scroll when top-level. Post message when embedded.
  const scrollToHash = (href: string) => {
    if (!href?.startsWith('#')) return false;
    const id = href.slice(1);

    // Embedded → ask parent to set hash (parent will update its URL and sync iframe)
    if (inIframe()) {
      postParent({ type: 'wet:set-hash', hash: `#${id}` });
      return true;
    }

    // Top-level behavior
    const el = document.getElementById(id);
    if (!el) return false;
    const header = document.querySelector('header') as HTMLElement | null;
    const offset = header?.offsetHeight ?? 72;
    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.history.replaceState(null, '', `#${id}`);
    window.scrollTo({ top: y, behavior: 'smooth' });
    ga('select_content', { content_type: 'anchor_scroll', item_id: id, menu_type: 'hash' });
    return true;
  };

  useEffect(() => {
    if (location.hash) scrollToHash(location.hash);
    const onHash = () => scrollToHash(location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const onAnchorClick = (href: string, label?: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (scrollToHash(href)) {
      e.preventDefault();
      setOpen(false);
      ga('select_content', {
        content_type: inIframe() ? 'nav_link_iframe' : 'nav_link',
        item_id: 'anchor',
        label: label || href,
        menu_type: 'header',
        link_url: href,
      });
    }
  };

  // Desktop hover open/close with delay
  const openNow = (id: number, label?: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setOpenDesktop(id);
    if (openDesktop !== id) ga('menu_open', { menu_type: 'desktop', label, depth: 0 });
  };
  const closeSoon = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpenDesktop(null), 180);
  };

  const DesktopMenu = ({ nodes }: { nodes: MenuNode[] }) => (
    <ul className="flex items-center space-x-8 relative top-[2px]">
      {nodes.map((n) => {
        const hasChildren = (n.children?.length ?? 0) > 0;
        const isOpen = openDesktop === n.id;
        const labelText = txt(n.title);
        return (
          <li
            key={n.id}
            className="relative"
            onMouseEnter={() => openNow(n.id, labelText)}
            onMouseLeave={closeSoon}
          >
            <a
              href={n.href}
              onClick={
                isAnchor(n.href)
                  ? onAnchorClick(n.href, labelText)
                  : () => {
                      setOpen(false);
                      ga('select_content', {
                        content_type: 'nav_link',
                        item_id: 'top',
                        label: labelText,
                        menu_type: 'desktop',
                        link_url: n.href,
                      });
                    }
              }
              target={isAnchor(n.href) ? undefined : '_top'}
              rel={isAnchor(n.href) ? undefined : 'noopener'}
              className="text-gray-300 hover:text-white inline-flex items-center gap-1"
            >
              <span dangerouslySetInnerHTML={{ __html: n.title }} />
              {hasChildren && (
                <span
                  className={`ml-1 text-[11px] opacity-70 transition-transform ${isOpen ? 'rotate-180 opacity-90' : ''}`}
                  aria-hidden="true"
                >
                  ▾
                </span>
              )}
            </a>
            {hasChildren && isOpen && (
              <div className="absolute left-0 mt-2 z-[10000]">
                <ul
                  className="min-w=[220px] rounded-xl bg-[#0f243a] shadow-xl p-2"
                  onMouseEnter={() => openNow(n.id, labelText)}
                  onMouseLeave={closeSoon}
                >
                  {n.children!.map((c) => {
                    const childLabel = txt(c.title);
                    return (
                      <li key={c.id}>
                        <a
                          href={c.href}
                          onClick={
                            isAnchor(c.href)
                              ? onAnchorClick(c.href, childLabel)
                              : () => {
                                  setOpen(false);
                                  ga('select_content', {
                                    content_type: 'nav_link',
                                    item_id: 'child',
                                    label: childLabel,
                                    menu_type: 'desktop',
                                    link_url: c.href,
                                  });
                                }
                          }
                          target={isAnchor(c.href) ? undefined : '_top'}
                          rel={isAnchor(c.href) ? undefined : 'noopener'}
                          className="block px-3 py-2 text-gray-300 hover:text-white whitespace-nowrap"
                          dangerouslySetInnerHTML={{ __html: c.title }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const toggleMobileParent = (id: number, label?: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      const willOpen = !next.has(id);
      if (willOpen) {
        ga('menu_open', { menu_type: 'mobile', label, depth: 0 });
        next.add(id);
      } else {
        ga('menu_close', { menu_type: 'mobile', label, depth: 0 });
        next.delete(id);
      }
      return next;
    });
  };
  const MobileMenu = ({ nodes, depth = 0 }: { nodes: MenuNode[]; depth?: number }) => (
    <ul className={`${depth === 0 ? 'space-y-2' : 'pl-3 space-y-1'}`}>
      {nodes.map((n) => {
        const hasChildren = (n.children?.length ?? 0) > 0;
        const openItem = openIds.has(n.id);
        const labelText = txt(n.title);
        return (
          <li key={n.id}>
            <div className="flex items-center justify-between">
              <a
                href={n.href}
                onClick={
                  isAnchor(n.href)
                    ? onAnchorClick(n.href, labelText)
                    : () => {
                        setOpen(false);
                        ga('select_content', {
                          content_type: hasChildren ? 'parent' : 'leaf',
                          item_id: hasChildren ? 'parent' : 'leaf',
                          label: labelText,
                          menu_type: 'mobile',
                          link_url: n.href,
                        });
                      }
                }
                target={isAnchor(n.href) ? undefined : '_top'}
                rel={isAnchor(n.href) ? undefined : 'noopener'}
                className="block text-gray-300 hover:text-white"
              >
                <span dangerouslySetInnerHTML={{ __html: n.title }} />
                {hasChildren && (
                  <span
                    className={`ml-1 text-[11px] opacity-70 transition-transform ${openItem ? 'rotate-180 opacity-90' : ''}`}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                )}
              </a>
              {hasChildren && (
                <button
                  type="button"
                  className="ml-3 text-gray-300"
                  aria-label="Toggle submenu"
                  aria-expanded={openItem ? 'true' : 'false'}
                  onClick={() => toggleMobileParent(n.id, labelText)}
                >
                  {openItem ? '▴' : '▾'}
                </button>
              )}
            </div>
            {hasChildren && openItem && (
              <div className="mt-1">
                <MobileMenu nodes={n.children!} depth={depth + 1} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0d1b2a]/90 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
        <a
          href={SECTION_ROOT}
          className="flex items-center gap-2"
          aria-label="Water Edge Technologies - Home"
          onClick={() =>
            ga('select_content', {
              content_type: 'logo',
              item_id: 'home_logo',
              menu_type: 'header',
              link_url: SECTION_ROOT,
            })
          }
        >
          <img src="/wet.svg" alt="Water Edge Technologies" className="h-8 w-auto" />
        </a>

        {/* Desktop */}
        <div className="hidden md:block">
          <DesktopMenu nodes={items} />
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => {
            setOpen((v) => {
              const next = !v;
              ga('menu_toggle', { menu_type: 'mobile', state: next ? 'open' : 'close' });
              return next;
            });
          }}
          className="md:hidden text-white"
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          <span className="sr-only">Toggle menu</span>☰
        </button>
      </nav>
      <div className="hidden md:flex items-center justify-center gap-6 bg-[#0b1a2a] text-gray-200 text-sm py-2 px-6 border-t border-white/5">
        <div className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-blue-400 shrink-0">
            <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd"/>
          </svg>
          <a href={phone.href} className="hover:text-white transition-colors">{phone.display}</a>
        </div>
        <span className="text-white/20">|</span>
        <div className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-blue-400 shrink-0">
            <path fillRule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-2.003 3.5-4.697 3.5-8.327a8 8 0 10-16 0c0 3.63 1.556 6.324 3.5 8.327a19.58 19.58 0 002.683 2.282 16.975 16.975 0 001.144.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
          </svg>
          <a href={address.href} className="hover:text-white transition-colors" target="_blank" rel="noopener">
            {address.text}
          </a>
        </div>
        <span className="text-white/20">|</span>
        <div className="flex items-center gap-3">
          {social.map((s) => (
            <a
              key={s.href}
              href={s.href}
              style={{ color: s.color }}
              className="opacity-85 hover:opacity-100 transition-opacity"
              target="_blank"
              rel="noopener"
              aria-label={s.label}
            >
              {s.icon}
            </a>
          ))}
        </div>
      </div>

      {/* Mobile */}
      {open && (
        <div id="mobile-nav" className="md:hidden px-6 pb-4 bg-[#0d1b2a]/90">
          <MobileMenu nodes={items} />
        </div>
      )}
    </header>
  );
};

export default Header;
